import type { JourneyPlanItem } from "@/lib/chat-timeline";
import { NEEDS_AI_DRAFT_BODY } from "@/lib/journey-touch-display";
import { journeyRequest } from "@/lib/journey-api";

export { NEEDS_AI_DRAFT_BODY };

/** 客户 ↔ 旅程绑定（对应 journey `CustomerBinding`） */
export type JourneyCustomerBinding = {
  id: number;
  account_id: number;
  brand_id: number;
  external_userid: string;
  journey_definition_id: number;
  status: "active" | "ended" | string;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
};

export type JourneyPlanStepStatus =
  | "planned"
  | "queued"
  | "sent"
  | "skipped"
  | "cancelled"
  | string;

export type JourneyPlanStep = {
  id: number;
  journey_node_id: number | null;
  position: number;
  /** YYYY-MM-DD */
  planned_on: string;
  planned_at: string | null;
  should_send: boolean;
  draft_content: string | null;
  rationale: string | null;
  /** 手册节点执行说明 / 计划意图（form→instruction，freeform→prompt） */
  instruction?: string | null;
  /** 同 instruction，兼容别名 */
  task_brief?: string | null;
  /** LLM 未生成客户文案的占位步 */
  needs_ai_draft?: boolean;
  /** 轻插入来源；非空则档点不自动捞 */
  insert_source?: "reschedule" | "replay" | null;
  /** 触达编排人群 id */
  reach_cohort_id?: number | null;
  /** cohort 模板 / 个文（优先于手册） */
  template_content?: string | null;
  personal_content?: string | null;
  outbound_content?: string | null;
  channel_hint: "proactive" | "group_send" | string;
  status: JourneyPlanStepStatus;
};

export type JourneyPlan = {
  id: number;
  version: number;
  status: "planning" | "ready" | "superseded" | "failed" | string;
  steps: JourneyPlanStep[];
};

export type ListCustomerBindingsOpts = {
  brandId: string | number;
  /** 默认仅 active；传 all 含已结束 */
  status?: "active" | "all";
  externalUserid?: string;
  journeyDefinitionId?: string | number;
};

/** 列出品牌下客户绑定 */
export function listCustomerBindings(
  accountId: string | number,
  opts: ListCustomerBindingsOpts,
) {
  const params = new URLSearchParams({
    brand_id: String(opts.brandId),
  });
  if (opts.status === "all") params.set("status", "all");
  if (opts.externalUserid) params.set("external_userid", opts.externalUserid);
  if (opts.journeyDefinitionId != null) {
    params.set("journey_definition_id", String(opts.journeyDefinitionId));
  }
  return journeyRequest<{ customer_bindings: JourneyCustomerBinding[] }>(
    accountId,
    `/customer_bindings?${params.toString()}`,
  );
}

/** 读取单个绑定 */
export function getCustomerBinding(accountId: string | number, bindingId: string | number) {
  return journeyRequest<JourneyCustomerBinding>(accountId, `/customer_bindings/${bindingId}`);
}

/** 绑定客户到已启用旅程（journey 侧会入队规划） */
export function createCustomerBinding(
  accountId: string | number,
  body: { journey_definition_id: number | string; external_userid: string },
) {
  return journeyRequest<JourneyCustomerBinding>(accountId, "/customer_bindings", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** 结束绑定并作废未发送计划步 */
export function destroyCustomerBinding(accountId: string | number, bindingId: string | number) {
  return journeyRequest<void>(accountId, `/customer_bindings/${bindingId}`, {
    method: "DELETE",
  });
}

/** 当前有效计划（ready）；无则 `{ plan: null }`（可能仍在 planning） */
export function getCustomerBindingPlan(accountId: string | number, bindingId: string | number) {
  return journeyRequest<{ plan: JourneyPlan | null }>(
    accountId,
    `/customer_bindings/${bindingId}/plan`,
  );
}

/** 仍待发送的计划步状态 */
const FUTURE_STEP_STATUSES = new Set(["planned", "queued"]);

/**
 * 将 API 计划步转为聊天时间线用的展示项。
 * 保留应发送且未完成的步；占位稿（needs_ai_draft）即使 should_send=false 也展示；
 * 普通 should_send=false 仍跳过。按 position / 计划时间排序。
 */
export function planStepsToFutureItems(
  plan: JourneyPlan,
  meta: { journeyId: number; journeyName: string },
  now: Date = new Date(),
): JourneyPlanItem[] {
  const today = startOfLocalDay(now);
  const items: JourneyPlanItem[] = [];

  for (const step of plan.steps ?? []) {
    const needsAi = Boolean(step.needs_ai_draft);
    // 普通不发送跳过；占位稿需展示「未经 AI」角标
    if (!step.should_send && !needsAi) continue;
    if (!FUTURE_STEP_STATUSES.has(step.status)) continue;

    const scheduledAt = stepScheduledIso(step);
    const plannedDay = parsePlannedOn(step.planned_on) ?? startOfLocalDay(new Date(scheduledAt));
    const offsetDays = Math.round(
      (plannedDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000),
    );

    items.push({
      id: `plan-step-${step.id}`,
      journeyId: meta.journeyId,
      journeyName: meta.journeyName,
      body: resolveStepBody(step, needsAi),
      instruction: resolveStepInstruction(step),
      offsetDays,
      scheduledAt,
      stepStatus: step.status,
      position: step.position,
      planStepId: step.id,
      preview: false,
      needsAiDraft: needsAi,
    });
  }

  return items.sort((a, b) => {
    const diff = Date.parse(a.scheduledAt) - Date.parse(b.scheduledAt);
    if (diff !== 0) return diff;
    return (a.position ?? 0) - (b.position ?? 0);
  });
}

/** 优先 cohort 模板/个文/outbound；占位步用短句，不把手册当正文 */
export function resolveStepBody(step: JourneyPlanStep, needsAi: boolean): string {
  if (needsAi) return NEEDS_AI_DRAFT_BODY;

  const personal = (step.personal_content ?? "").trim();
  if (personal) return personal;
  const template = (step.template_content ?? "").trim();
  if (template) return template;
  const outbound = (step.outbound_content ?? "").trim();
  if (outbound) return outbound;
  const draft = (step.draft_content ?? "").trim();
  return draft || NEEDS_AI_DRAFT_BODY;
}

/** 手册执行说明（任务区）；与客户正文分离 */
export function resolveStepInstruction(step: JourneyPlanStep): string | null {
  const text = (step.instruction ?? step.task_brief ?? "").trim();
  return text || null;
}

/** 从计划步列表建 stepId → 执行说明（含已发送步，供出站条补任务区） */
export function buildInstructionByStepId(
  steps: JourneyPlanStep[] | null | undefined,
): Record<number, string> {
  const map: Record<number, string> = {};
  for (const step of steps ?? []) {
    const text = resolveStepInstruction(step);
    if (text) map[step.id] = text;
  }
  return map;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** planned_on（YYYY-MM-DD）→ 本地日 0 点 */
function parsePlannedOn(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function stepScheduledIso(step: JourneyPlanStep): string {
  if (step.planned_at) {
    const ms = Date.parse(step.planned_at);
    if (Number.isFinite(ms)) return new Date(ms).toISOString();
  }
  const day = parsePlannedOn(step.planned_on);
  if (day) return day.toISOString();
  return new Date().toISOString();
}
